/* tutor-2 (C.2, C.4, C.6): LA TARJETA FIJADA de a media sesion: lo que el alumno tiene enfrente, dentro del tutor.

   ARCHIVO EN ASCII A PROPOSITO.

   Tarjeta interior (#101010, borde #1F1F1F, esquinas 14, relleno 10 12) que se pliega y se despliega con un toque en
   toda la tarjeta. Arriba, la etiqueta (11 px, mayusculas, naranja suave) y a la derecha "Ver pregunta" / "Ver
   reverso" con la flecha hacia abajo (plegada) u "Ocultar" con la flecha hacia arriba (desplegada). Plegada, el texto
   recortado a dos renglones; desplegada, entero. Debajo, separado por una linea #1F1F1F:
   - Flashcards (`tipo: flashcards`): "Tarjeta 4 de 20", el frente; desplegada, el reverso. "Ver reverso" lo ensena
     AQUI (no voltea la flashcard de verdad).
   - Adaptive ANTES de contestar: "Pregunta 7", el enunciado y, siempre, el renglon naranja del modo pista.
     DESPUES: "Tu respuesta: B" (rojo si fallo, verde si acerto) y "Correcta: D" (verde), con el texto de la opcion.
   - Resultados del examen (`tipo: exam`, el examen entero): arriba el renglon del puntaje (el texto de la pantalla,
     `subtituloDelPuntaje`), "Pregunta 7" con el tache rojo o la palomita verde de la revision, el enunciado, "Tu
     respuesta" (rojo; si no contesto, no va) y "Correcta" (verde).
   Las letras salen del indice de la opcion (A, B, C, D), como el servidor las escribe al modelo.
   En la pagina grande (C.6) va plegada encima del hilo, sin cerrar.

   alumno-m4 (5.1, pedido de Carlos del 22-oct): PLEGADA ES SOLO EL RENGLON DE ARRIBA (la etiqueta, con el tache o la
   palomita del examen, y la accion con su flecha): ni puntaje, ni enunciado, ni reverso, ni respuestas. La UNICA
   excepcion es el renglon naranja del modo pista en Adaptive antes de contestar, que es el aviso del modo y se queda.
   Desplegada, todo lo de antes. El estado vive aqui y la tarjeta no se re-crea al cambiar `contexto` (misma posicion
   en el padre, y en la pagina la `key` es la conversacion), asi que cambiar de pregunta no la vuelve a plegar. */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, ChevronDown, ChevronUp, XCircle } from 'lucide-react';
import { subtituloDelPuntaje } from '../../lib/subtituloDelPuntaje';

const LETRAS = 'ABCDEFGHIJ';

/** La letra de una respuesta: un indice (0 -> A), una letra suelta, o el texto de una de las opciones. */
export function letraDe(valor, opciones = []) {
  if (valor === null || valor === undefined || valor === '' || typeof valor === 'boolean') return null;
  if (typeof valor === 'number') return valor >= 0 && valor < LETRAS.length ? LETRAS[valor] : null;
  const s = String(valor).trim();
  if (s.length === 1 && LETRAS.includes(s.toUpperCase())) return s.toUpperCase();
  const i = (opciones || []).findIndex((o) => String(o).trim() === s);
  return i >= 0 && i < LETRAS.length ? LETRAS[i] : null;
}

function textoDeLaOpcion(letra, opciones = []) {
  const i = letra ? LETRAS.indexOf(letra) : -1;
  return i >= 0 && opciones && opciones[i] != null ? String(opciones[i]) : '';
}

/** La pregunta fijada del examen entero (la `fijada`, o la primera). */
export function preguntaFijada(contexto) {
  const preguntas = (contexto && contexto.preguntas) || [];
  const n = Number(contexto && contexto.fijada) || 1;
  return preguntas.find((p) => Number(p.n) === n) || preguntas[0] || null;
}

function Renglon({ etiqueta, letra, opciones, color, testid }) {
  if (!letra) return null;
  const texto = textoDeLaOpcion(letra, opciones);
  return (
    <div className="min-w-0" data-testid={testid}>
      <span className={color}>{etiqueta}</span>{texto ? <span>, {texto}</span> : null}
    </div>
  );
}

export default function TarjetaFijada({ contexto, plegadaDeInicio = true }) {
  const { t } = useTranslation(['study']);
  const [abierta, setAbierta] = useState(!plegadaDeInicio);
  if (!contexto || !contexto.tipo) return null;
  const tipo = contexto.tipo;
  const examen = tipo === 'exam';
  const p = examen ? preguntaFijada(contexto) : contexto;
  if (!p) return null;
  const opciones = (p && p.opciones) || [];
  let etiqueta;
  let texto;
  if (tipo === 'flashcards') {
    etiqueta = t('study:tutor.mid.card', { n: contexto.n || 1, total: contexto.total || 1 });
    texto = contexto.frente || '';
  } else {
    etiqueta = t('study:tutor.mid.question', { n: (examen ? p.n : contexto.n) || 1 });
    texto = p.enunciado || '';
  }
  const accion = abierta
    ? t('study:tutor.mid.hide')
    : (tipo === 'flashcards' ? t('study:tutor.mid.show_back') : t('study:tutor.mid.show_question'));
  const Flecha = abierta ? ChevronUp : ChevronDown;
  const contestada = examen || (tipo === 'adaptive' && Boolean(contexto.contestada));
  const elegida = contestada ? letraDe(p.elegida, opciones) : null;
  const correcta = contestada ? letraDe(p.correcta, opciones) : null;
  const acerto = examen ? Boolean(p.bien) : (elegida && correcta && elegida === correcta);
  const alternar = () => setAbierta((v) => !v);
  return (
    <div role="button" tabIndex={0} aria-expanded={abierta} onClick={alternar}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); alternar(); } }}
      className="shrink-0 cursor-pointer rounded-[14px] bg-[#101010] border border-[#1F1F1F] px-3 py-2.5 text-left outline-none focus-visible:ring-1 focus-visible:ring-[#3a3a3a]"
      data-testid="tutor-tarjeta-fijada" data-tipo={tipo} data-abierta={abierta ? 'true' : 'false'}>
      {examen && abierta ? (
        <div className="text-[12px] text-[#9A9A9A] mb-1.5" data-testid="tutor-fijada-puntaje">
          {subtituloDelPuntaje(t, 'study:exam.results.score_subtitle', contexto.correctas, contexto.total)}
        </div>
      ) : null}
      <div className="flex items-center gap-1.5 text-[11px] tracking-[.06em] uppercase font-semibold text-[#F7A06A]">
        <span className="truncate" data-testid="tutor-fijada-etiqueta">{etiqueta}</span>
        {examen ? (
          <span className="inline-flex shrink-0" data-testid={p.bien ? 'tutor-fijada-bien' : 'tutor-fijada-mal'}>
            {p.bien
              ? <CheckCircle2 className="w-3.5 h-3.5 shrink-0 text-[#4ADE80]" aria-hidden="true" />
              : <XCircle className="w-3.5 h-3.5 shrink-0 text-[#F87171]" aria-hidden="true" />}
          </span>
        ) : null}
        <span className="ml-auto shrink-0 inline-flex items-center gap-1 normal-case tracking-normal font-medium text-[12px] text-[#9A9A9A]"
          data-testid="tutor-fijada-accion">
          {accion}
          <Flecha className="w-3.5 h-3.5" aria-hidden="true" />
        </span>
      </div>
      {abierta ? (
        <div className="mt-1 text-[14px] leading-[1.4] text-[#F4F4F3] break-words" data-testid="tutor-fijada-texto">
          {texto}
        </div>
      ) : null}
      {tipo === 'flashcards' && abierta ? (
        <div className="mt-2 pt-2 border-t border-[#1F1F1F] text-[13px] leading-[1.45] text-[#C8C8C8] break-words" data-testid="tutor-fijada-reverso">
          {contexto.reverso || ''}
        </div>
      ) : null}
      {tipo === 'adaptive' && !contestada ? (
        <div className="mt-2 pt-2 border-t border-[#1F1F1F] text-[13px] leading-[1.45] text-[#F7A06A]" data-testid="tutor-fijada-pista">
          {t('study:tutor.mid.hint_only')}
        </div>
      ) : null}
      {contestada && abierta ? (
        <div className="mt-2 pt-2 border-t border-[#1F1F1F] text-[13px] leading-[1.45] text-[#C8C8C8] grid gap-0.5" data-testid="tutor-fijada-respuestas">
          <Renglon etiqueta={t('study:tutor.mid.yours', { letter: elegida })} letra={elegida} opciones={opciones}
            color={acerto ? 'text-[#4ADE80]' : 'text-[#F87171]'} testid="tutor-fijada-tuya" />
          <Renglon etiqueta={t('study:tutor.mid.correct', { letter: correcta })} letra={correcta} opciones={opciones}
            color="text-[#4ADE80]" testid="tutor-fijada-correcta" />
        </div>
      ) : null}
    </div>
  );
}
